import SourceFlowHead from "@sourceflow-uk/sourceflow-head";
import metaObject from "@/../.sourceflow/metadata.json";
import { useMemo } from "react";

const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL;
const SITE_NAME = process.env.NEXT_PUBLIC_SITE_NAME;
const SITE_DESCRIPTION = process.env.NEXT_PUBLIC_SITE_DESCRIPTION;
const TWITTER_HANDLE = process.env.NEXT_PUBLIC_TWITTER_HANDLE;
const PUBLIC_LOGO = process.env.NEXT_PUBLIC_LOGO;

export const defaultProps = {
  metaObject,
  siteName: SITE_NAME,
  title: SITE_NAME,
  titleTemplate: `%s`,
  defaultTitle: SITE_NAME,
  description: SITE_DESCRIPTION,
  images: [
    {
      url: PUBLIC_LOGO,
    },
  ],
  twitter: {
    site: TWITTER_HANDLE,
    cardType: "summary_large_image",
  },
};

const getStringOrEmpty = (value) => (typeof value === "string" ? value : "");

const toAbsoluteUrl = (value) => {
  const url = getStringOrEmpty(value);

  if (!url) {
    return "";
  }

  if (url.startsWith("http")) {
    return url;
  }

  if (!BASE_URL) {
    return url;
  }

  return `${BASE_URL.replace(/\/$/, "")}/${url.replace(/^\//, "")}`;
};

// eslint-disable-next-line import/no-anonymous-default-export,react/display-name
export default function ({
  title,
  robots,
  keywords,
  alternate = { url: "", hreflang: "" },
  canonical: __canonical = "",
  description,
  open_graph,
  json_schema,
}) {
  const resolvedTitle = getStringOrEmpty(title) || SITE_NAME || "";
  const resolvedDescription = getStringOrEmpty(description) || SITE_DESCRIPTION || "";

  const canonical = useMemo(() => {
    const url = toAbsoluteUrl(__canonical);

    if (!url) {
      return "";
    }

    return url.endsWith("/") ? url : `${url}/`;
  }, [__canonical]);

  const languageAlternates = useMemo(() => {
    if (alternate?.hreflang && alternate?.url) {
      return [{ hrefLang: alternate.hreflang, href: alternate.url }];
    }

    return [];
  }, [alternate?.hreflang, alternate?.url]);

  const openGraphImage = toAbsoluteUrl(open_graph?.image?.url ?? open_graph?.image ?? PUBLIC_LOGO);

  return (
    <>
      <SourceFlowHead {...defaultProps}>
        <title>{resolvedTitle}</title>
        <meta key="description" name="description" content={resolvedDescription} />
        {canonical && <link key="canonical" rel="canonical" href={canonical} />}
        {robots && <meta key="robots" name="robots" content={robots} />}
        {keywords && <meta key="keywords" name="keywords" content={keywords} />}
        <meta key="og:title" property="og:title" content={resolvedTitle} />
        <meta key="og:description" property="og:description" content={resolvedDescription} />
        {canonical && <meta key="og:url" property="og:url" content={canonical} />}
        {SITE_NAME && <meta key="og:site_name" property="og:site_name" content={SITE_NAME} />}
        <meta key="og:type" property="og:type" content="website" />
        {openGraphImage && <meta key="og:image" property="og:image" content={openGraphImage} />}
        <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
        {TWITTER_HANDLE && <meta key="twitter:site" name="twitter:site" content={TWITTER_HANDLE} />}
        <meta key="twitter:title" name="twitter:title" content={resolvedTitle} />
        <meta key="twitter:description" name="twitter:description" content={resolvedDescription} />
        {openGraphImage && <meta key="twitter:image" name="twitter:image" content={openGraphImage} />}
        {languageAlternates.map(({ hrefLang, href }) => (
          <link key={`alternate-${hrefLang}-${href}`} rel="alternate" hrefLang={hrefLang} href={href} />
        ))}
        {json_schema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: json_schema }} />}
      </SourceFlowHead>
    </>
  );
}
